import type {
  AriaAttributes,
  ChangeEvent,
  KeyboardEvent,
  KeyboardEventHandler,
  MouseEvent,
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
  Ref,
} from 'react'
import React, { useEffect, useRef } from 'react'
import { useDisposables } from '../hooks/useDisposables'
import { useSyncRefs } from '../hooks/useSyncRefs'
import ExpandMore from '../icons/ExpandMore'
import { ActionTypes as MenuActionTypes } from '../Menu/Actions'
import { useMenu } from '../Menu/Menu'
import type SvgIcon from '../SvgIcon/SvgIcon'
import type {
  TextInputColor,
  TextInputSize,
  TextInputVariant,
} from '../TextInput/styles'
import TextInput from '../TextInput/TextInput'
import { Focus } from '../utilities/calculateActiveIndex'
import { Keys } from '../utilities/keyboard'
import { ActionTypes } from './Actions'
import { useSelect } from './Select'

export type TextProps = {
  color?: TextInputColor
  dataSet?: Record<string, string | undefined>
  defaultValue?: string
  disabled?: boolean
  displayInputRef: Ref<HTMLInputElement>
  error?: boolean
  helperText?: string
  inputRef: Ref<HTMLInputElement>
  label: string
  name?: string
  onChange?: (
    event: ChangeEvent<HTMLInputElement>,
    actions: Record<string, () => void>,
  ) => void
  revert: () => () => void
  size?: TextInputSize
  startAdornment?: typeof SvgIcon | ReactNode
  variant?: TextInputVariant
}

interface TextPassThruProps {
  'aria-controls': AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup': 'listbox'
  'aria-labelledby': AriaAttributes['aria-labelledby']
  id: string
  inputRef?: Ref<HTMLInputElement>
  name?: string
  onClick: MouseEventHandler<HTMLInputElement>
  onKeyDown: KeyboardEventHandler<HTMLInputElement>
  role: 'button'
}

function Text({
  color,
  dataSet,
  defaultValue,
  disabled,
  displayInputRef,
  error,
  helperText,
  inputRef,
  label,
  onChange,
  name,
  revert,
  size,
  startAdornment,
  variant,
}: TextProps): JSX.Element {
  const d = useDisposables()
  const [, selectDispatch] = useSelect()
  const [state, dispatch] = useMenu()
  const labelRef = useRef<HTMLLabelElement>(null)
  const containerRef = useSyncRefs(
    state.buttonRef as MutableRefObject<HTMLLabelElement>,
    labelRef,
  )

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist()

    selectDispatch({
      type: ActionTypes.SelectOption,
      value: event.target.value,
    })

    onChange?.(event, { revert: revert() })
  }

  const menuProps: TextPassThruProps = {
    'aria-controls': `coconut-menu-${state.id}`,
    'aria-expanded': state.show ? 'true' : undefined,
    'aria-haspopup': 'listbox',
    'aria-labelledby': `label-coconut-menu-button-${state.id} coconut-menu-button-${state.id}`,
    id: `coconut-menu-button-${state.id}`,
    onClick: (event: MouseEvent<HTMLInputElement>) => {
      if (state.show) {
        dispatch({ type: MenuActionTypes.CloseMenu })
        d.nextFrame(() => {
          state.buttonRef.current?.focus({ preventScroll: true })
        })
      } else {
        event.preventDefault()
        dispatch({ type: MenuActionTypes.OpenMenu })
        d.nextFrame(() => {
          state.itemsRef.current?.focus({ preventScroll: true })
        })
      }
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (event.repeat) {
        return
      }

      switch (event.key) {
        case Keys.Space:
        case Keys.Enter:
        case Keys.ArrowDown:
          event.preventDefault()
          dispatch({ type: MenuActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            if (!state.listbox || !state.selectedItem?.index) {
              dispatch({ type: MenuActionTypes.GoToItem, focus: Focus.First })
            }
          })
          break

        case Keys.ArrowUp:
          event.preventDefault()
          dispatch({ type: MenuActionTypes.OpenMenu })
          d.nextFrame(() => {
            state.itemsRef.current?.focus({ preventScroll: true })
            if (!state.listbox || !state.selectedItem?.index) {
              dispatch({ type: MenuActionTypes.GoToItem, focus: Focus.Last })
            }
          })
      }
    },
    role: 'button',
  }

  useEffect(() => {
    dispatch?.({ type: MenuActionTypes.RegisterSelect })
  }, [dispatch])

  return (
    <>
      <input
        className="hidden"
        data-select
        defaultValue={defaultValue}
        disabled={disabled}
        name={name}
        onChange={handleChange}
        ref={inputRef}
      />
      <TextInput
        appearFocused={state.show}
        color={color}
        containerRef={containerRef}
        dataSet={dataSet}
        disabled={disabled}
        endAdornment={ExpandMore}
        error={error}
        helperText={helperText}
        inputRef={displayInputRef}
        label={label}
        readOnly
        selectable
        size={size}
        startAdornment={startAdornment}
        variant={variant}
        {...menuProps}
      />
    </>
  )
}

export default Text
